import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { nanoid } from 'nanoid';

const MODAL_ID_PARAM = 'modalId';
const MODAL_ID_LENGTH = 3;

function getModalIdsFromParams(params: URLSearchParams): Set<string> {
  const ids = params.get(MODAL_ID_PARAM)?.split(',').filter(Boolean) ?? [];
  return new Set(ids);
}

function updateUrlWithModalIds(
  params: URLSearchParams,
  modalIds: Set<string>
): void {
  const modalIdsArray = Array.from(modalIds).filter(Boolean);
  if (modalIdsArray.length > 0) {
    params.set(MODAL_ID_PARAM, modalIdsArray.join(','));
  } else {
    params.delete(MODAL_ID_PARAM);
  }
}

/**
 * Keeps modalId in sync with the URL based on open state.
 * Handles multiple modals switching without relying on previous refs only.
 */
export function useModalUrlSync(
  isModalOpen: boolean,
  customModalId?: string
): void {
  const [searchParams, setSearchParams] = useSearchParams();

  const modalIdRef = useRef<string>(customModalId ?? nanoid(MODAL_ID_LENGTH));
  const lastModalIdRef = useRef<string | null>(null);
  const lastIsOpenRef = useRef<boolean>(false);

  // Update modalIdRef if customModalId changes
  useEffect(() => {
    if (customModalId) {
      modalIdRef.current = customModalId;
    }
  }, [customModalId]);

  useEffect(() => {
    const currentModalId = modalIdRef.current;
    const lastIsOpen = lastIsOpenRef.current;
    const modalIds = getModalIdsFromParams(searchParams);
    const newParams = new URLSearchParams(searchParams);

    if (isModalOpen && !lastIsOpen) {
      modalIds.add(currentModalId);
      updateUrlWithModalIds(newParams, modalIds);
      setSearchParams(newParams, { replace: true });
    }

    if (!isModalOpen && lastIsOpen && lastModalIdRef.current) {
      modalIds.delete(lastModalIdRef.current);
      updateUrlWithModalIds(newParams, modalIds);
      setSearchParams(newParams, { replace: true });
    }

    lastIsOpenRef.current = isModalOpen;
    lastModalIdRef.current = currentModalId;
  }, [isModalOpen, searchParams, setSearchParams]);
}
